import React, { useCallback, useState } from 'react';
import { reportsAPI } from '../../../services/api';
import useReport from '../shared/useReport';
import KpiStrip from '../shared/KpiStrip';
import ReportTable from '../shared/ReportTable';
import PrintButton from '../shared/PrintButton';
import DateRangeFilter, { getPresetRange } from '../shared/DateRangeFilter';
import { formatClinicDate, formatClinicDateTime, formatClinicTime } from '../../../utils/clinicTime';
import { REVIEW_LABEL, describePosition } from '../../attendance/attendanceStatus';
import { kpiMap } from './kpis';

// Every punch made outside the hospital's range, and what was decided about it. The deciding itself
// happens on the attendance board, where the selfies are; this is the paper trail, for a month or a
// year at a time.

const STATUSES = [
  { key: '', label: 'All' },
  { key: 'FLAGGED', label: 'Still to review' },
  { key: 'ACCEPTED', label: 'Accepted' },
  { key: 'REJECTED', label: 'Rejected' },
];

const CHIP = {
  FLAGGED: 'bg-amber-100 text-amber-800',
  ACCEPTED: 'bg-emerald-100 text-emerald-800',
  REJECTED: 'bg-red-100 text-red-800',
};

const time = (v) => formatClinicTime(v, { hour: 'numeric', minute: '2-digit', hour12: true }, '-');

// The same wording the review screen uses. It reads the whole side, because a punch whose position
// was recorded but never checked is not the same as one with no position at all, and saying "No
// position" for both hid a real reading from whoever was reviewing it.
const where = (side) => (side ? describePosition(side).verdict : '-');

/** The side of an entry in the shape describePosition wants, from the report's flat row. */
const sideOf = (r, which) => ({
  latitude: r[`${which}Latitude`] ?? (r[`${which}DistanceMeters`] != null || r[`${which}WithinRange`] != null ? 0 : null),
  longitude: r[`${which}Longitude`] ?? 0,
  distanceMeters: r[`${which}DistanceMeters`],
  withinRange: r[`${which}WithinRange`],
  accuracyMeters: r[`${which}AccuracyMeters`] ?? null,
  approximate: !!r[`${which}Approximate`],
});

const COLUMNS = [
  {
    key: 'workDate', label: 'Day',
    render: (r) => formatClinicDate(r.workDate, { weekday: 'short', day: 'numeric', month: 'short' }),
    csv: (r) => r.workDate,
  },
  { key: 'userName', label: 'Name', wrap: true },
  { key: 'clockInAt', label: 'In', render: (r) => time(r.clockInAt), csv: (r) => r.clockInAt },
  { key: 'clockOutAt', label: 'Out', render: (r) => time(r.clockOutAt), csv: (r) => r.clockOutAt },
  {
    key: 'inDistanceMeters', label: 'Punched in', wrap: true,
    render: (r) => <span className={r.inWithinRange === false ? 'text-amber-700' : ''}>{where(sideOf(r, 'in'))}</span>,
    csv: (r) => where(sideOf(r, 'in')),
  },
  {
    key: 'outDistanceMeters', label: 'Punched out', wrap: true,
    render: (r) => (r.clockOutAt ? <span className={r.outWithinRange === false ? 'text-amber-700' : ''}>{where(sideOf(r, 'out'))}</span> : '-'),
    csv: (r) => (r.clockOutAt ? where(sideOf(r, 'out')) : ''),
  },
  {
    key: 'reviewStatus', label: 'Decision',
    render: (r) => (
      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${CHIP[r.reviewStatus] || 'bg-gray-100 text-gray-600'}`}>
        {REVIEW_LABEL[r.reviewStatus] || r.reviewStatus}
      </span>
    ),
    csv: (r) => r.reviewStatus,
  },
  {
    key: 'reviewedByName', label: 'Decided by', wrap: true,
    render: (r) => (r.reviewedByName ? `${r.reviewedByName}, ${formatClinicDateTime(r.reviewedAt)}` : '-'),
    csv: (r) => r.reviewedByName || '',
  },
  { key: 'reviewNote', label: 'Note', wrap: true, render: (r) => r.reviewNote || '-' },
];

const AttendanceFlaggedReport = () => {
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');
  const [status, setStatus] = useState('');

  const fetcher = useCallback(
    () => reportsAPI.getAttendanceFlagged({ from: range.from, to: range.to, status }),
    [range, status],
  );
  const { data, loading } = useReport(fetcher);

  const rows = data?.rows || [];
  const k = kpiMap(data);
  const waiting = k.flagged ?? rows.filter((r) => r.reviewStatus === 'FLAGGED').length;
  const kpis = [
    { label: 'Flagged punches', value: k.total ?? rows.length, sub: 'Made outside the hospital, or with no position' },
    { label: 'Still to review', value: waiting, sub: 'Nobody has decided yet', tone: waiting > 0 ? 'alert' : undefined },
    { label: 'Accepted', value: k.accepted ?? rows.filter((r) => r.reviewStatus === 'ACCEPTED').length, sub: 'Counted as attendance' },
    { label: 'Rejected', value: k.rejected ?? rows.filter((r) => r.reviewStatus === 'REJECTED').length, sub: 'Not counted at all' },
    { label: 'People', value: k.people ?? new Set(rows.map((r) => r.userId)).size, sub: 'Staff with a flagged punch' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <div className="flex gap-2 flex-wrap" role="group" aria-label="Decision">
        {STATUSES.map((s) => (
          <button
            key={s.key || 'all'}
            type="button"
            aria-pressed={status === s.key}
            onClick={() => setStatus(s.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${status === s.key ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <ReportTable
        title="Punches made away from the hospital"
        columns={COLUMNS}
        rows={rows}
        csvName={`attendance-flagged-${range.from}-to-${range.to}`}
        emptyText="No punch was flagged in the selected range"
        footnote="A punch is flagged when it was made further from the hospital than the allowed distance, or with no position at all. It counts as attendance until somebody rejects it."
      />
    </div>
  );
};

export default AttendanceFlaggedReport;
